import { Box } from '@mui/material';
import {
  ReadOnly,
  useEntityPermission,
  useUserAccess,
} from '@ui-framework/index';
import { forwardRef, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import AdvancedAutoComplete, {
  createDebouncedLoader,
} from '../AdvancedAutoComplete';
import useAddressValidation from './useAddressValidation';

const formatAddress = ({ address, city, state, country, zip }) => {
  const array = [];
  if (address) {
    array.push(address);
  }
  if (city) {
    array.push(city);
  }
  if (state) {
    array.push(state);
  }
  if (country) {
    array.push(country);
  }
  if (zip) {
    array.push(zip);
  }
  if (array?.length > 0) {
    return array.join(', ');
  }
  return null;
};

export const getCurrentAddress = addressObj => {
  const currentFormattedAddress = formatAddress(addressObj);
  const currentAddress = currentFormattedAddress
    ? {
        id: addressObj?.id || null,
        address: currentFormattedAddress || '',
      }
    : null;

  return currentAddress;
};

const ValidatedAddress = forwardRef(
  ({ overwriteProps, minChars = 2, ...props }, ref) => {
    const labelkey = 'address';
    const valueKey = 'id';

    const googleSesssionTokenName =
      props?.field?.registerDependencies?.googleSesssionToken;

    if (!googleSesssionTokenName) {
      throw new Error(
        'Please add field.registerDependencies.googleSesssionToken in schema for Address Field type',
      );
    }

    const userAccess = useUserAccess();
    const { isHidden, isReadOnly } = useEntityPermission();
    const { getValues, control, setValue } = useFormContext();
    const { createSession, getAddress } = useAddressValidation();

    const currentAddress = getValues(props?.name);

    const googleSesssionTokenField = useWatch({
      control,
      name: googleSesssionTokenName,
    });

    const loadOptions = createDebouncedLoader({
      minChars,
      delay: 400,
      loader: async ({ query }) => {
        const result = await getAddress({
          query,
          sessionToken: googleSesssionTokenField,
        });

        if (result?.ok) {
          return { data: result.data, total: result.data.length };
        }

        return { data: [], total: 0 };
      },
    });

    const registerSessionToken = async () => {
      const session = await createSession();
      setValue(googleSesssionTokenName, session, { shouldDirty: false });
    };

    const initialize = async () => {
      registerSessionToken();
    };

    /*
    Since we are regisetering the token field at run time, reset action wipes it out
    So we need additional steps to re-register it
    */
    useEffect(() => {
      if (!userAccess?.isUserReadonly() && !googleSesssionTokenField) {
        initialize();
      }
    }, [googleSesssionTokenField]);

    if (isReadOnly(props?.permission, currentAddress?.[valueKey])) {
      return (
        <ReadOnly name={props?.name}>{currentAddress?.[labelkey]}</ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return (
        <input
          type="hidden"
          name={props?.name}
          value={currentAddress?.[valueKey] || ''}
        />
      );
    }

    return (
      <Box>
        <Controller
          control={control}
          defaultValue=""
          name={googleSesssionTokenName}
          render={() => null}
        />
        {googleSesssionTokenField && (
          <Controller
            control={control}
            name={props?.name}
            render={({ field, fieldState: { error } }) => {
              return (
                <AdvancedAutoComplete
                  forwardedRef={ref}
                  defaultOption={currentAddress}
                  labelkey={labelkey}
                  valueKey={valueKey}
                  minChars={props?.field?.minChars || minChars}
                  onChange={option => {
                    field.onChange(option);
                  }}
                  placeholder={props?.placeholder}
                  loadOptions={loadOptions}
                  {...field}
                  error={error}
                />
              );
            }}
          />
        )}
      </Box>
    );
  },
);

export default ValidatedAddress;
